/*
 * =============================================================================
 * FILE HEADER — packages/ir/src/schemas/project-config.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed              |
 * |---|------------------------|-------------------|-----------------|---------------------------|
 * | 1 | 2026-06-19 (Sprint A)  | Claude Sonnet 4.6 | Project config  | Created — mimiq.config.json schema |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/ir/src/index.ts           (re-exported)
 * - packages/cli/src/config-loader.ts  (loads mimiq.config.json)
 * - apps/studio/src/components/SettingsPage.tsx (UI display)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. All fields are optional. Missing fields fall back to IR-level config or defaults.
 * 2. CLI merges project config → IR config → CLI flags (later wins).
 * 3. `env` values override IR config.env at run time — safe for per-machine secrets.
 * 4. `dataSources.basePath` is a convenience prefix applied to all relative CSV/JSON paths.
 *
 * =============================================================================
 */
import { z } from "zod";
export const ProjectConfigSchema = z.object({
    /** Human name for the project — shown in reports. */
    project: z.string().optional(),
    /** Description shown in test reports and Studio. */
    description: z.string().optional(),
    /** Default app to test (overridden per IR if set). */
    app: z.object({
        packageName: z.string().optional(),
        bundleId: z.string().optional(),
    }).optional(),
    /**
     * Environment variables available as ${env:KEY} in all tests.
     * Override per test in IR config.env.
     */
    env: z.record(z.string()).default({}),
    /**
     * Timeout defaults. Individual IR files can override these.
     * stepTimeout: max ms per step (default 15000).
     * suiteTimeout: wall-clock limit for entire suite (optional).
     */
    timeouts: z.object({
        stepMs: z.number().positive().default(15_000),
        suiteMs: z.number().positive().optional(),
    }).default({}),
    /**
     * Reporting behaviour. These apply globally unless overridden.
     * screenshotOnFailure: capture screenshot on every failed step (default true).
     * screenshotOnStep:    capture screenshot after EVERY step (default false; heavy).
     * videoPolicy:         "never" | "onFailure" | "always" (default "never").
     */
    reporting: z.object({
        screenshotOnFailure: z.boolean().default(true),
        screenshotOnStep: z.boolean().default(false),
        videoPolicy: z.enum(["never", "onFailure", "always"]).default("never"),
    }).default({}),
    /**
     * Where to find data files (CSV, JSON) used by forEachRow.
     * basePath: directory prefix for relative CSV/JSON paths.
     *   e.g. basePath = "./test-data" → path "users.csv" resolves to "./test-data/users.csv"
     */
    dataSources: z.object({
        basePath: z.string().default("./test-data"),
    }).default({}),
});
/** Returns a safe-defaults ProjectConfig (all optional fields filled). */
export function defaultProjectConfig() {
    return ProjectConfigSchema.parse({});
}
//# sourceMappingURL=project-config.js.map